import { Requestor, StorageBackend } from '@openid/appauth';
import { AuthActions, AuthService, Browser } from 'ionic-appauth';

import { isRecoverableRefreshError } from 'src/app/common/authentication/refresh-error';

/** Storage key ionic-appauth uses for the persisted token response. */
const TOKEN_RESPONSE_KEY = 'token_response';

/**
 * ionic-appauth's `AuthService`, hardened against the library's most destructive habit (#26615).
 *
 * The stock implementation deletes the persisted token response on *every* failed refresh, with
 * no distinction between "the server rejected the grant" and "the device had no network for a
 * moment". A single dead zone therefore destroys a perfectly valid refresh token and costs the
 * user a full password login. The library offers no hook or configuration to change this, so the
 * smallest safe intervention is this subclass; `authFactory` below provides it app-wide for the
 * ionic-appauth `AuthService` DI token (see `MobileOAuthModule`).
 *
 * It has to be a subclass — not logic in the app's own `AuthService` — because the library calls
 * `refreshToken()` *internally* as well: `getValidToken()` refreshes on its own once the token
 * has less than ten minutes left, on a path no app code ever sees (this is exactly the trigger
 * behind the field logouts in #26615). Overriding here is the single choke point every refresh
 * passes through, ours and the library's own.
 *
 * Should ionic-appauth ever learn to keep the token on transient failures itself, this class
 * shrinks back to `removeToken()`.
 */
export class MobileTokenPreservingAuthService extends AuthService {
  /**
   * Whether a RefreshFailed action fired in the current refresh cycle. Tracked separately from
   * the error value: the action carries `error.message`, which is undefined for shapeless
   * rejections — a missing error must never look like a missing failure.
   */
  private _refreshFailed = false;
  private _lastRefreshError: unknown = null;

  constructor(browser: Browser, storage: StorageBackend, requestor: Requestor) {
    super(browser, storage, requestor);

    // The library reports the refresh error only through the action stream, and it does so
    // before refreshToken() returns — so remember it here to be able to classify it below.
    this.events$.subscribe(action => {
      if (action?.action === AuthActions.RefreshFailed) {
        this._refreshFailed = true;
        this._lastRefreshError = action.error;
      }
    });
  }

  async removeToken(): Promise<void> {
    await this.storage.removeItem(TOKEN_RESPONSE_KEY);
  }

  /**
   * Keeps the stored token response alive across transient refresh failures (#26615).
   *
   * `AuthService.refreshToken()` in ionic-appauth removes the token response on *any* error:
   *
   *     yield this.requestTokenRefresh().catch(response => {
   *       this.storage.removeItem(TOKEN_RESPONSE_KEY);
   *       this.notifyActionListers(AuthActionBuilder.RefreshFailed(response));
   *     });
   *
   * A single moment without network therefore destroys a perfectly valid refresh token and
   * forces the user to log in again with a password. This override restores the backup unless
   * the authorization server actually rejected the grant (`invalid_grant`), in which case the
   * token stays removed — that logout is correct.
   *
   * The trigger for a restore is the `RefreshFailed` action, never the storage content: the
   * library removes the token in exactly the code path that fires the action, so "no action"
   * means "nothing was deleted". The storage cannot answer that question —
   * `CapacitorSecureStorage.getItem()` reports read errors as `null`, and a transient keychain
   * read failure right after a *successful* refresh would otherwise make us overwrite the fresh
   * token with the stale backup. With rotating refresh tokens that stale token is already
   * consumed, and the next refresh would end in the very `invalid_grant` logout this class
   * exists to prevent.
   *
   * The library also fires `removeItem()` without awaiting it, so the storage may still read
   * "token present" while the removal is in flight. The restore therefore never checks the
   * storage after the refresh either — rewriting the backup is idempotent when the removal
   * failed, and it wins the race when the removal has not landed yet.
   *
   * `loadTokenFromStorage()` is called after a restore so the in-memory session matches the
   * storage again: the failed refresh already pushed `undefined` into the token subject, and
   * without re-hydrating it `requestTokenRefresh()` would fail with "No Token Defined!" on
   * every subsequent attempt. `internalLoadTokenFromStorage()` only parses and notifies — it
   * does not drop expired tokens — so this cannot delete what we just restored.
   */
  public async refreshToken(): Promise<void> {
    this._refreshFailed = false;
    this._lastRefreshError = null;

    const backup = await this.readStoredTokenResponse();

    await super.refreshToken();

    if (!this._refreshFailed) {
      // No RefreshFailed action fired in this cycle, so the library removed nothing and there
      // is nothing to repair. events$ delivers synchronously (a plain rxjs subject), so a
      // failure of the awaited super call above is always visible here.
      return;
    }

    if (backup == null) {
      // Either nothing was stored, or the keychain read failed — the storage backend reports
      // both as null, and we cannot protect what we could not read. Handling read errors
      // properly is branch 08's job; until then the case must at least be visible in Sentry,
      // otherwise "the fix did not apply" and "the fix was not needed" look identical.
      this.reportRefreshOutcome('no-backup');
      return;
    }

    if (!isRecoverableRefreshError(this._lastRefreshError)) {
      this.reportRefreshOutcome('token-discarded');
      return;
    }

    try {
      await this.storage.setItem(TOKEN_RESPONSE_KEY, backup);
      await this.loadTokenFromStorage();
      this.reportRefreshOutcome('token-restored');
    } catch (error) {
      // Restoring is best effort: if it fails we are exactly where the library left us.
      window.logger?.error('MobileTokenPreservingAuthService failed to restore the token response', error);
    }
  }

  private async readStoredTokenResponse(): Promise<string | null> {
    try {
      return (await this.storage.getItem(TOKEN_RESPONSE_KEY)) ?? null;
    } catch {
      return null;
    }
  }

  private reportRefreshOutcome(outcome: 'token-restored' | 'token-discarded' | 'no-backup'): void {
    try {
      window.logger?.addBreadcrumb({
        category: 'auth',
        message: `[26615] refresh failed | outcome=${outcome}`,
      });
    } catch {
      // telemetry must never break the auth flow
    }
  }
}

export const authFactory = (requestor: Requestor, browser: Browser, storage: StorageBackend) => {
  return new MobileTokenPreservingAuthService(browser, storage, requestor);
};
